import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Button, TableCell, TableRow, Box, Chip, CircularProgress, Tooltip, IconButton, Link } from '@mui/material';
import { Add as AddIcon, Groups as GroupsIcon, Visibility as VisibilityIcon } from '@mui/icons-material';

import CommonDataTable from '@libComponents/table/CommonDataTable';
import { apiClient } from '@libs/apiRequest';
import { theme } from 'app/Theme';
import * as actions from '../redux.actions';
import * as selectors from '../redux.selectors';
import { ACTIVE_STATUS_FILTER_OPTIONS, ROWS_PER_PAGE_OPTIONS, TABLE_COLUMNS } from '../common';
import TeamTableBody from './TeamTableBody';
import StatusChip from './StatusChip';
import TeamDialog from './TeamDialog';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';

const TeamManagement = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const teams = useSelector(selectors.teamsSelector);
    const pagination = useSelector(selectors.paginationSelector);
    const filters = useSelector(selectors.filtersSelector);
    const sort = useSelector(selectors.sortSelector);
    const lastCreatedTeamId = useSelector(selectors.lastCreatedTeamIdSelector);

    const [searchInput, setSearchInput] = useState('');
    const [unassignedCount, setUnassignedCount] = useState(0);
    const [unassignedLoading, setUnassignedLoading] = useState(false);
    const initialUrlSynced = useRef(false);

    // Sync URL search params to Redux filters on mount
    useEffect(() => {
        if (!initialUrlSynced.current) {
            initialUrlSynced.current = true;
            const search = searchParams.get('search') || '';
            const isActive = searchParams.get('isActive') ?? '';
            const offset = parseInt(searchParams.get('page') || '1', 10);
            const limit = parseInt(searchParams.get('limit') || '10', 10);
            const sortBy = searchParams.get('sortBy') || 'updatedAt';
            const sortDescending = searchParams.get('sortDescending') !== 'false';

            dispatch(actions.setFilters({ search, isActive }));
            dispatch(actions.setPagination({ offset, limit }));
            dispatch(actions.setSort({ sortBy, sortDescending }));
            setSearchInput(search);
        }
    }, [searchParams, dispatch]);

    // Sync Redux state changes to URL search params
    useEffect(() => {
        if (!initialUrlSynced.current) return;
        const params = new URLSearchParams();
        if (filters.search) params.set('search', filters.search);
        if (filters.isActive !== '' && filters.isActive !== undefined && filters.isActive !== null) {
            params.set('isActive', String(filters.isActive));
        }
        if (pagination.offset && pagination.offset > 1) params.set('page', String(pagination.offset));
        if (pagination.limit && pagination.limit !== 10) params.set('limit', String(pagination.limit));
        if (sort.sortBy) params.set('sortBy', sort.sortBy);
        if (sort.sortDescending !== undefined) params.set('sortDescending', String(sort.sortDescending));
        setSearchParams(params, { replace: true });
    }, [filters, pagination.offset, pagination.limit, sort, setSearchParams]);

    useEffect(() => { setSearchInput(filters.search); }, [filters.search]);
    useEffect(() => { dispatch(actions.fetchTeams()); }, [dispatch]);

    // Fetch unassigned employees count
    useEffect(() => {
        let cancelled = false;
        const fetchUnassigned = async () => {
            setUnassignedLoading(true);
            try {
                const response = await apiClient.get('/team/unassigned/employees', { params: { limit: 1 } });
                if (!cancelled) setUnassignedCount(parseInt(response?.data?.total) || 0);
            } catch {
                if (!cancelled) setUnassignedCount(0);
            } finally {
                if (!cancelled) setUnassignedLoading(false);
            }
        };
        fetchUnassigned();
        return () => { cancelled = true; };
    }, []);

    // Auto-redirect to newly-created team
    useEffect(() => {
        if (lastCreatedTeamId) {
            const id = lastCreatedTeamId;
            dispatch(actions.clearLastCreatedTeam());
            navigate(`/teams/${id}`);
        }
    }, [lastCreatedTeamId, dispatch, navigate]);

    const handleSearch = useCallback(
        (v) => {
            dispatch(actions.setFilters({ search: v }));
            if (pagination.offset !== 1) dispatch(actions.setPagination({ offset: 1 }));
        },
        [dispatch, pagination.offset]
    );

    const handleActiveChange = (newValue) => {
        dispatch(actions.setFilters({ isActive: newValue ? newValue.value : '' }));
        if (pagination.offset !== 1) dispatch(actions.setPagination({ offset: 1 }));
    };
    const handlePageChange = (_, newPage) => dispatch(actions.setPagination({ offset: newPage }));
    const handleRowsPerPageChange = (value) =>
        dispatch(actions.setPagination({ limit: parseInt(value, 10), offset: 1 }));
    const handleSort = useCallback(
        (columnId) => {
            const same = sort.sortBy === columnId;
            dispatch(actions.setSort({ sortBy: columnId, sortDescending: same ? !sort.sortDescending : false }));
            if (pagination.offset !== 1) dispatch(actions.setPagination({ offset: 1 }));
        },
        [dispatch, sort.sortBy, sort.sortDescending, pagination.offset]
    );
    const handleCreate = () => dispatch(actions.openTeamDialog());
    const handleClearFilters = () => {
        setSearchInput('');
        dispatch(actions.setFilters({ search: '', isActive: '' }));
        dispatch(actions.setSort({ sortBy: 'updatedAt', sortDescending: true }));
        dispatch(actions.setPagination({ limit: 10, offset: 1 }));
    };

    const selectedActiveOption =
        filters.isActive !== '' && filters.isActive !== null && filters.isActive !== undefined
            ? ACTIVE_STATUS_FILTER_OPTIONS.find((opt) => String(opt.value) === String(filters.isActive)) || null
            : null;

    const filterConfigs = [
        {
            id: 'status',
            label: 'Status',
            options: ACTIVE_STATUS_FILTER_OPTIONS,
            value: selectedActiveOption,
            onChange: handleActiveChange,
            minWidth: 130
        }
    ];

    // Special "Unassigned" pinned row rendered before regular team rows
    const renderUnassignedRow = () => (
        <TableRow
            key='__unassigned__'
            hover
            onClick={(e) => {
                if (e.target.closest('button, a, input, [role="button"]')) return;
                if (e.metaKey || e.ctrlKey) {
                    window.open('/teams/unassigned', '_blank');
                } else {
                    navigate('/teams/unassigned');
                }
            }}
            onAuxClick={(e) => {
                if (e.button === 1) {
                    if (e.target.closest('button, a, input, [role="button"]')) return;
                    window.open('/teams/unassigned', '_blank');
                }
            }}
            sx={{
                cursor: 'pointer',
                '&:last-child td': { border: 0 },
                backgroundColor: (t) => t.palette.action.hover,
                '&:hover': { backgroundColor: (t) => t.palette.action.selected }
            }}
        >
            <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GroupsIcon sx={{ color: (t) => t.palette.text.secondary, fontSize: 20 }} />
                    <Link
                        component={RouterLink}
                        to='/teams/unassigned'
                        color='inherit'
                        underline='hover'
                        sx={{ fontWeight: 'bold' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        Unassigned
                    </Link>
                </Box>
            </TableCell>
            <TableCell>Employees not assigned to any team or client</TableCell>
            <TableCell align='center'>
                {unassignedLoading ? (
                    <CircularProgress size={18} />
                ) : (
                    <Chip label={unassignedCount} size='small' variant='outlined' sx={{ fontWeight: 600 }} />
                )}
            </TableCell>
            <TableCell align='center'><StatusChip active={true} /></TableCell>
            <TableCell>-</TableCell>
            <TableCell align='center'>
                <Tooltip title='View Unassigned'>
                    <IconButton component={RouterLink} to='/teams/unassigned' size='small' onClick={(e) => e.stopPropagation()}>
                        <VisibilityIcon />
                    </IconButton>
                </Tooltip>
            </TableCell>
        </TableRow>
    );

    return (
        <CommonDataTable
            // Header
            title='Teams'
            searchName='team-search'
            searchPlaceholder='Search by name or description...'
            searchValue={searchInput}
            onSearch={handleSearch}
            primaryAction={
                <Button
                    variant='contained'
                    startIcon={<AddIcon />}
                    onClick={handleCreate}
                    size='small'
                    sx={{ height: '34px', minHeight: '34px' }}
                >
                    Create Team
                </Button>
            }
            // Filters
            filters={filterConfigs}
            onResetFilters={handleClearFilters}
            // Sorting
            sortField={sort.sortBy}
            sortDescending={sort.sortDescending}
            onSort={handleSort}
            // Data & columns
            columns={TABLE_COLUMNS}
            data={teams}
            totalRows={pagination.total}
            // Pagination
            page={pagination.offset}
            pageSize={pagination.limit}
            pageSizeOptions={ROWS_PER_PAGE_OPTIONS}
            pageSizeName='teamManagement'
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            // Custom row rendering: prepend the Unassigned row, then regular rows
            renderRow={(row, idx) =>
                idx === 0 ? (
                    <>
                        {renderUnassignedRow()}
                        <TeamTableBody key={row.id || idx} team={row} />
                    </>
                ) : (
                    <TeamTableBody key={row.id || idx} team={row} />
                )
            }
            emptyState={renderUnassignedRow()}
        >
            <TeamDialog />
            <DeleteConfirmationDialog />
        </CommonDataTable>
    );
};

export default TeamManagement;
